import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';
import Cookies from 'js-cookie';

import {useCountryRedirectSuggestionEnabled, useLocalesMgr} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import globalMessage from 'brand/app/components/messages';
import Button from 'brandComponents/Button';
import CountryLanguageSelector from 'brandComponents/CountryLanguageSelector';
import Popup from 'brandComponents/Popup';
import Typography from 'brandComponents/Typography';

import {ICountryRedirectPopupProps, POPUP_COOKIE_NAME} from './ICountryRedirectPopup';
import messages from './messages';

import styles from './CountryRedirectPopup.module.scss';

const CountryRedirectPopup = ({classNames}: ICountryRedirectPopupProps) => {
	const {managerRepository} = useGeneralProvider();
	const localesMgr = useLocalesMgr();
	const suggestionEnabled = useCountryRedirectSuggestionEnabled();
	const cookiesLocale = localesMgr.getLocaleFromCookies();
	const intl = useIntl();
	const [showOtherCountries, setShowOtherCountries] = useState(false);
	const [targetCountry, setTargetCountry] = useState<string>(null);
	const currentLocale = localesMgr.getCurrentLocale();
	const currentCountryCode = currentLocale.getCountryCode();
	const currentCookieValue = Cookies.get(POPUP_COOKIE_NAME);
	// Sometime the currentCountryCode is null, and message should be alway {id: string}
	const countryMessage =  globalMessage[currentCountryCode] ?? {id: `country.${currentCountryCode}`};
	const currentCountryName = intl.formatMessage(countryMessage);
	// Sometime the targetCountry is null, and message should be alway {id: string}
	const targetCountryMessage = globalMessage[targetCountry] ?? {id: `country.${targetCountry}`};
	const userCountryName = intl.formatMessage(targetCountryMessage);

	useEffect(() => {
		if (suggestionEnabled && !currentCookieValue && targetCountry && targetCountry !== currentCountryCode) {
			managerRepository.getPopupsMgr().openPopup('countryRedirect');
		}
	}, [targetCountry, currentCountryCode, currentCookieValue, suggestionEnabled]);

	const handleRedirectFunc = async () => {
		if (!suggestionEnabled) {
			return;
		}

		const geolocation = await localesMgr.fetchGeolocation();
		setTargetCountry(geolocation.country.getCountryCode());
	};

	useEffect(() => {
		handleRedirectFunc();
	}, [currentCookieValue, cookiesLocale.isValid()]);

	const handleClickYes = useCallback(() => {
		Cookies.set(POPUP_COOKIE_NAME, 'true', {expires: 365});
		managerRepository.getPopupsMgr().closePopup('countryRedirect');
	}, []);

	const handleClickNo = useCallback(() => {
		if (targetCountry) {
			const countries = managerRepository.getCountriesMgr().getCountries();
			const countrySelect = countries?.find((countryModel) => countryModel.getCountryCode() === targetCountry);
			const previousCountry =
				countries?.find((countryModel) => countryModel.getCountryCode() === currentCountryCode);
			const availableLanguageArr = countrySelect.getAvailableLanguages();
			const availableLanguage = availableLanguageArr.map(langCode => ({
				label: globalMessage[langCode]?.defaultMessage[0].value,
				value: langCode
			}));
			const isDifferentSiteId = countrySelect.getSiteId() !== previousCountry.getSiteId();
			localesMgr.updateLocale(targetCountry, availableLanguage[0].value);
			localesMgr.updateUrl(isDifferentSiteId ? '/' : null, !isDifferentSiteId);

			Cookies.set(POPUP_COOKIE_NAME, 'true', {expires: 365});
			managerRepository.getPopupsMgr().closePopup('countryRedirect');
		}
	}, [targetCountry]);

	const handleOtherCountries = () => setShowOtherCountries(true);

	return (
		<Popup
			id="countryRedirect"
			onClose={handleClickYes}
			overlayClassName={cn(styles['country-redirect-popup'], classNames)}
		>
			{!showOtherCountries ? (
				<>
					<Typography classNames={styles['country-redirect-popup__title']}>
						{intl.formatMessage(messages.greeting)}
					</Typography>

					<Typography classNames={styles['country-redirect-popup__title']}>
						{intl.formatMessage(messages.heading, {
							currentCountry: currentCountryName,
						})}
					</Typography>

					<div className="d-flex flex-col ai-start">
						<Button classNames={styles['country-redirect-popup__button']}
							variant="primary"
							onClick={handleClickYes}>
							{intl.formatMessage(messages.yesButton, {
								currentCountry: currentCountryName,
							})}
						</Button>

						{targetCountry ?
							<Button
								classNames={styles['country-redirect-popup__button']}
								onClick={handleClickNo}
								variant="primary-white"
							>
								{intl.formatMessage(messages.noButton, {
									userCountry: userCountryName,
								})}
							</Button>
							:
							null
						}

						<Button
							classNames={styles['country-redirect-popup__button']}
							onClick={handleOtherCountries}
							variant="tertiary"
							showAsLink
						>
							{intl.formatMessage(messages.seeMoreButton)}
						</Button>
					</div>
				</>
			) : (
				<>
					<Typography variant="h5">{intl.formatMessage(messages.selectCountry)}</Typography>

					<CountryLanguageSelector
						classNames={{
							wrapper: styles['country-redirect-popup__language-wrapper'],
							menu: styles['country-redirect-popup__language-menu'],
							button: styles['country-redirect-popup__language-submit'],
						}}
						popupsShouldBeClosed
					/>
				</>
			)}
		</Popup>
	);
};

export default CountryRedirectPopup;
